'use client';

/**
 * InlineSearch Component
 *
 * An inline search input with dropdown results.
 * Similar to the notification dropdown UX - types in the input,
 * results slide down below.
 *
 * Features:
 * - Inline input that shows in the header
 * - Results dropdown slides down on focus/search
 * - Keyboard navigation (arrows, Enter, Escape)
 * - Large loading spinner for clear feedback
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useId,
} from 'react';
import { cn } from '@/lib/utils';
import {
  Search,
  Loader2,
  X,
  Command,
  ArrowUp,
  ArrowDown,
  CornerDownLeft,
  AlertCircle,
  Maximize2,
  Settings2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Check,
} from 'lucide-react';
import { useSearchAiz } from '../hooks/useSearchAiz';
import { useSearchAizSettings } from '../hooks/useSearchAizSettings';
import { useDataSources } from '../providers';
import { getTileComponent } from '../tiles';
import { FullSearchModal } from './FullSearchModal';
import { getDataSource } from '../utils/dataSourceRegistry';
import { isSafeNonSearchUrl, navigateToSafeUrl } from '../utils/safeNavigation';
import type { SearchResult, TileAction, RerankConfig } from '../types';

// =============================================================================
// TYPES
// =============================================================================

export interface InlineSearchProps {
  /** Placeholder text */
  placeholder?: string;
  /** Initial query to run on mount or URL change */
  initialQuery?: string;
  /** Callback when a result is selected */
  onSelect?: (result: SearchResult) => void;
  /** Additional CSS classes for the container */
  className?: string;
  /** Additional CSS classes for the input */
  inputClassName?: string;
}

// =============================================================================
// KEYBOARD SHORTCUT HOOK
// =============================================================================

export interface UseInlineSearchOptions {
  onOpen?: () => void;
}

export function useInlineSearch(options: UseInlineSearchOptions = {}) {
  const { onOpen } = options;
  const [isFocused, setIsFocused] = useState(false);

  // Listen for Cmd/Ctrl + K
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        onOpen?.();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onOpen]);

  return {
    isFocused,
    setIsFocused,
    open: () => setIsFocused(true),
    close: () => setIsFocused(false),
  };
}

// =============================================================================
// LOADING SPINNER
// =============================================================================

function LoadingSpinner() {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-4">
      <div className="relative">
        {/* Outer glow ring */}
        <div className="absolute inset-0 rounded-full bg-cyan-500/20 blur-xl animate-pulse" />
        {/* Main spinner */}
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
      </div>
      <p className="text-sm text-slate-400 animate-pulse">Searching...</p>
    </div>
  );
}

// =============================================================================
// KEYBOARD HINTS
// =============================================================================

function KeyboardHint({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="px-1.5 py-0.5 text-xs font-mono bg-slate-800 border border-slate-700 rounded text-slate-400">
      {children}
    </kbd>
  );
}

function ShortcutHints({ hasSelection }: { hasSelection?: boolean }) {
  return (
    <div className="flex items-center gap-4 text-xs text-slate-500">
      <span className="flex items-center gap-1">
        <KeyboardHint><ArrowUp className="w-3 h-3" /></KeyboardHint>
        <KeyboardHint><ArrowDown className="w-3 h-3" /></KeyboardHint>
        <span>Navigate</span>
      </span>
      <span className="flex items-center gap-1">
        <KeyboardHint><CornerDownLeft className="w-3 h-3" /></KeyboardHint>
        <span>{hasSelection ? 'Open' : 'Full search'}</span>
      </span>
      <span className="flex items-center gap-1">
        <KeyboardHint>Esc</KeyboardHint>
        <span>Close</span>
      </span>
    </div>
  );
}

// =============================================================================
// SOURCE CHIP
// =============================================================================

interface SourceChipProps {
  sourceId: string;
  isActive: boolean;
  onClick: () => void;
}

function SourceChip({ sourceId, isActive, onClick }: SourceChipProps) {
  const source = getDataSource(sourceId);
  if (!source) return null;

  const Icon = source.icon;

  return (
    <button aria-pressed={isActive}
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all duration-150',
        'border',
        isActive
          ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300'
          : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:border-slate-600 hover:text-slate-300'
      )}
    >
      <Icon className={cn('w-3 h-3', isActive ? source.iconColor : '')} />
      <span>{source.name}</span>
      {isActive && <Check className="w-3 h-3 text-cyan-400" />}
    </button>
  );
}

// =============================================================================
// AI TOGGLE
// =============================================================================

interface AiToggleProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  tier?: string;
}

function AiToggle({ label, isActive, onClick, tier }: AiToggleProps) {
  return (
    <button aria-pressed={isActive}
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all duration-150',
        'border',
        isActive
          ? 'bg-violet-500/20 border-violet-500/40 text-violet-300'
          : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:border-slate-600 hover:text-slate-300'
      )}
    >
      <Sparkles className={cn('w-3 h-3', isActive ? 'text-violet-400' : '')} />
      <span>{label}</span>
      {tier && isActive && (
        <span className="text-[10px]">({tier})</span>
      )}
    </button>
  );
}

// =============================================================================
// QUICK OPTIONS PANEL
// =============================================================================

interface QuickOptionsProps {
  isExpanded: boolean;
  onToggle: () => void;
  selectedSources: string[];
  onToggleSource: (sourceId: string) => void;
  rerankEnabled: boolean;
  onToggleRerank: () => void;
  rerankTier?: 'fast' | 'standard' | 'quality';
  availableSources: string[];
}

function QuickOptions({
  isExpanded,
  onToggle,
  selectedSources,
  onToggleSource,
  rerankEnabled,
  onToggleRerank,
  rerankTier,
  availableSources,
}: QuickOptionsProps) {
  // Show first 6 sources max for compact view
  const displaySources = availableSources.slice(0, 6);

  return (
    <div className="border-b border-slate-700/50">
      {/* Toggle Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-xs text-slate-400 hover:text-slate-300 hover:bg-slate-800/30 transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <Settings2 className="w-3.5 h-3.5" />
          <span>Quick Options</span>
          <span className="text-slate-600">•</span>
          <span className="text-cyan-400">{selectedSources.length} sources</span>
          {rerankEnabled && (
            <>
              <span className="text-slate-600">•</span>
              <span className="text-violet-400">AI on</span>
            </>
          )}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-3.5 h-3.5" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5" />
        )}
      </button>

      {/* Expanded Options */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 animate-in slide-in-from-top-1 duration-150">
          {/* Sources */}
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
              Search Sources
            </div>
            <div className="flex flex-wrap gap-1.5">
              {displaySources.map((sourceId) => (
                <SourceChip
                  key={sourceId}
                  sourceId={sourceId}
                  isActive={selectedSources.includes(sourceId)}
                  onClick={() => onToggleSource(sourceId)}
                />
              ))}
            </div>
          </div>

          {/* AI Features */}
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
              AI Features
            </div>
            <div className="flex flex-wrap gap-1.5">
              <AiToggle
                label="Rerank"
                isActive={rerankEnabled}
                onClick={onToggleRerank}
                tier={rerankTier}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function InlineSearch({
  placeholder = 'Search...',
  initialQuery = '',
  onSelect,
  className,
  inputClassName,
}: InlineSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isFullModalOpen, setIsFullModalOpen] = useState(false);
  const [isOptionsExpanded, setIsOptionsExpanded] = useState(false);

  // Get settings with safe fallbacks
  const { settings, isLoading: settingsLoading } = useSearchAizSettings();
  const { accessibleSources } = useDataSources();
  const availableSources = useMemo(
    () => accessibleSources.map((source) => source.id),
    [accessibleSources]
  );
  const fallbackSourceId = availableSources[0] ?? 'knowledge_base';

  // Safe access to settings with fallbacks
  const defaultSources = useMemo(() => {
    const configuredSources = settings?.sources?.default ?? ['knowledge_base'];
    const filteredSources = configuredSources.filter((sourceId) =>
      availableSources.includes(sourceId)
    );
    return filteredSources.length > 0 ? filteredSources : [fallbackSourceId];
  }, [availableSources, fallbackSourceId, settings?.sources?.default]);
  const resultsPerPage = settings?.results?.perPage ?? 10;
  const showScores = settings?.results?.showScores ?? false;
  const showSources = settings?.results?.showSources ?? true;
  const defaultRerankEnabled = settings?.ai?.rerank?.enabled ?? false;
  const defaultRerankTier = settings?.ai?.rerank?.tier ?? 'standard';

  // Local overrides for quick options
  const [selectedSources, setSelectedSources] = useState<string[]>(defaultSources);
  const [localRerankEnabled, setLocalRerankEnabled] = useState(defaultRerankEnabled);
  const appliedInitialQueryRef = useRef<string | null>(null);

  // Sync with settings when they load
  useEffect(() => {
    if (!settingsLoading) {
      setSelectedSources(defaultSources);
    }
    if (!settingsLoading && settings?.ai?.rerank) {
      setLocalRerankEnabled(settings.ai.rerank.enabled ?? false);
    }
  }, [defaultSources, settings, settingsLoading]);

  // Build rerank config from local state
  const rerankConfig: RerankConfig | undefined = localRerankEnabled
    ? { enabled: true, tier: defaultRerankTier, scoreThreshold: 0.3 }
    : undefined;

  // Search state with dynamic sources
  const {
    query,
    isSearching,
    results: rawResults,
    error,
    notices,
    selectedIndex,
    search,
    clear,
    navigateSelection,
    activateSelected,
  } = useSearchAiz({
    sources: selectedSources.length > 0 ? selectedSources : [fallbackSourceId],
    pattern: 'unified',
    limit: resultsPerPage,
    rerank: rerankConfig,
    debounceMs: 300,
    onResultSelect: onSelect,
  });

  useEffect(() => {
    const normalizedInitialQuery = initialQuery.trim();
    if (!normalizedInitialQuery) {
      appliedInitialQueryRef.current = null;
      return;
    }
    if (appliedInitialQueryRef.current === normalizedInitialQuery) {
      return;
    }

    appliedInitialQueryRef.current = normalizedInitialQuery;
    setIsDropdownOpen(true);
    void search(normalizedInitialQuery);
  }, [initialQuery, search]);

  // Toggle source selection
  const handleToggleSource = useCallback((sourceId: string) => {
    setSelectedSources(prev => {
      if (!availableSources.includes(sourceId)) {
        return prev;
      }
      if (prev.includes(sourceId)) {
        // Don't allow removing last source
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== sourceId);
      }
      return [...prev, sourceId];
    });
  }, [availableSources]);

  // Toggle rerank
  const handleToggleRerank = useCallback(() => {
    setLocalRerankEnabled(prev => !prev);
  }, []);

  // Toggle options panel
  const handleToggleOptions = useCallback(() => {
    setIsOptionsExpanded(prev => !prev);
  }, []);

  // Always ensure results is a safe array
  const results = Array.isArray(rawResults) ? rawResults : [];

  // Show dropdown when open and has query or results
  // Note: isFocused only controls input styling, not dropdown visibility
  // Dropdown closes via handleClickOutside, not blur
  const shouldShowDropdown = isDropdownOpen && (query.length > 0 || results.length > 0);
  const activeResult = !isSearching && shouldShowDropdown && selectedIndex >= 0
    ? results[selectedIndex] : undefined;
  const optionId = (resultId: string) => `${listboxId}-option-${encodeURIComponent(resultId)}`;

  // Focus input on Cmd/Ctrl + K
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setIsDropdownOpen(true);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0 && results.length > 0) {
      const items = listRef.current.querySelectorAll('[role="option"]');
      const selectedEl = items[selectedIndex] as HTMLElement;
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, results.length]);

  /**
   * Handle input change
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      search(e.target.value);
      setIsDropdownOpen(true);
    },
    [search]
  );

  /**
   * Open the full search modal
   */
  const handleOpenFullModal = useCallback(() => {
    setIsDropdownOpen(false);
    setIsFullModalOpen(true);
  }, []);

  /**
   * Close the full search modal
   */
  const handleCloseFullModal = useCallback(() => {
    setIsFullModalOpen(false);
    // Refocus the input
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          navigateSelection('down');
          break;
        case 'ArrowUp':
          e.preventDefault();
          navigateSelection('up');
          break;
        case 'Enter':
          e.preventDefault();
          // If a result is selected, call onSelect but DON'T navigate away
          // Navigation should be handled by the consumer if needed
          if (selectedIndex >= 0 && results[selectedIndex]) {
            const result = results[selectedIndex];
            onSelect?.(result);
            // Only navigate if result has a specific URL (not a search page)
            // and the URL doesn't start with /search
            if (isSafeNonSearchUrl(result.url)) {
              navigateToSafeUrl(result.url);
            }
            setIsDropdownOpen(false);
          } else {
            // Otherwise, open the full search modal
            handleOpenFullModal();
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsDropdownOpen(false);
          break;
      }
    },
    [navigateSelection, selectedIndex, results, onSelect, handleOpenFullModal]
  );

  /**
   * Handle result click
   * Only navigates if the result has a specific URL (not a /search page)
   */
  const handleResultClick = useCallback(
    (result: SearchResult) => {
      onSelect?.(result);
      // Only navigate if result has a specific URL that's not a search page
      if (isSafeNonSearchUrl(result.url)) {
        navigateToSafeUrl(result.url);
      }
      setIsDropdownOpen(false);
    },
    [onSelect]
  );

  /**
   * Handle tile action
   * Only navigates if the URL is not a /search page
   */
  const handleTileAction = useCallback(
    (action: TileAction) => {
      if (action.type === 'view' && action.url) {
        // Don't navigate to /search pages
        if (isSafeNonSearchUrl(action.url)) {
          navigateToSafeUrl(action.url);
          setIsDropdownOpen(false);
        }
      } else if (action.type === 'copyLink' && action.url) {
        navigator.clipboard.writeText(action.url);
      }
    },
    []
  );

  /**
   * Handle focus
   */
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setIsDropdownOpen(true);
  }, []);

  /**
   * Handle blur - only updates styling state
   * Dropdown visibility is controlled by handleClickOutside, not blur
   */
  const handleBlur = useCallback(() => {
    // Small delay to check if focus moved within container
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsFocused(false);
      }
    }, 100);
  }, []);

  /**
   * Clear input
   */
  const handleClear = useCallback(() => {
    clear();
    inputRef.current?.focus();
  }, [clear]);

  // results is already guaranteed to be a safe array from line 187

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Search Input */}
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all duration-200',
          isFocused
            ? 'bg-cyan-500/10 border-cyan-500/30 ring-1 ring-cyan-500/20'
            : 'bg-white/[0.04] border-white/[0.08] hover:border-white/[0.15]',
          inputClassName
        )}
      >
        {isSearching ? (
          <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
        ) : (
          <Search
            className={cn(
              'w-4 h-4 transition-colors duration-200',
              isFocused ? 'text-cyan-400' : 'text-white/70'
            )}
          />
        )}
        <input aria-label="Search"
          role="combobox"
          aria-expanded={shouldShowDropdown}
          aria-controls={shouldShowDropdown ? listboxId : undefined}
          aria-activedescendant={activeResult ? optionId(activeResult.id) : undefined}
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-28 lg:w-36 xl:w-48 bg-transparent text-sm text-white placeholder-white/40 focus:outline-none"
          autoComplete="off"
        />
        {/* Keyboard shortcut hint */}
        {!isFocused && !query && (
          <div className="hidden lg:flex items-center gap-0.5 text-[10px] text-white/60">
            <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] font-mono">
              <Command className="w-3 h-3 inline" />K
            </kbd>
          </div>
        )}
        {/* Clear button */}
        {query && (
          <button aria-label="Clear search"
            type="button"
            onClick={handleClear}
            className="p-0.5 rounded hover:bg-white/[0.1] text-white/70 hover:text-white transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {shouldShowDropdown && (
        <div
          className={cn(
            'absolute top-full left-0 right-0 mt-2 z-50',
            'bg-slate-900/95 backdrop-blur-xl border border-slate-700/50 rounded-xl shadow-2xl',
            'min-w-[360px] max-w-[520px] max-h-[70vh] overflow-hidden',
            'animate-in fade-in-0 slide-in-from-top-2 duration-200'
          )}
        >
          {/* Quick Options Panel */}
          <QuickOptions
            isExpanded={isOptionsExpanded}
            onToggle={handleToggleOptions}
            selectedSources={selectedSources}
            onToggleSource={handleToggleSource}
            rerankEnabled={localRerankEnabled}
            onToggleRerank={handleToggleRerank}
            rerankTier={defaultRerankTier}
            availableSources={availableSources}
          />

          {/* Loading State */}
          {isSearching && (
            <LoadingSpinner />
          )}

          {!error && !isSearching && notices.length > 0 && (
            <div data-testid="search-aiz-notices" role="status" className="px-4 py-2 space-y-1 text-xs text-amber-300">
              {notices.map((notice) => <p key={notice}>{notice}</p>)}
              {results.length === 0 && <p>Nothing was found in the sources that could be searched.</p>}
            </div>
          )}

          {/* Error State */}
          {!isSearching && error && (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <div className="p-3 rounded-full bg-red-500/10 mb-3">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <p className="text-sm font-medium text-red-400 mb-1">Search failed</p>
              <p className="text-xs text-slate-500">{error.message}</p>
            </div>
          )}

          {/* No Results */}
          {!isSearching && !error && notices.length === 0 && results.length === 0 && query.length >= 2 && (
            <div className="px-4 py-8 text-center text-slate-500">
              <Search className="w-8 h-8 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No results found for "{query}"</p>
            </div>
          )}

          {/* Type to Search */}
          {!isSearching && !error && notices.length === 0 && results.length === 0 && query.length < 2 && query.length > 0 && (
            <div className="px-4 py-8 text-center text-slate-500">
              <p className="text-sm">Type at least 2 characters to search...</p>
            </div>
          )}

          {/* Initial State */}
          {!isSearching && !error && notices.length === 0 && results.length === 0 && query.length === 0 && (
            <div className="px-4 py-8 text-center text-slate-500">
              <Search className="w-8 h-8 mx-auto mb-3 opacity-50" />
              <p className="text-sm mb-1">Type to search...</p>
              <p className="text-xs text-slate-600">Press Enter for full search with filters</p>
              <div className="mt-4">
                <ShortcutHints hasSelection={false} />
              </div>
            </div>
          )}

          {/* Results */}
          {(isSearching || results.length === 0) && <div id={listboxId} role="listbox" />}
          {!isSearching && results.length > 0 && (
            <>
              <div ref={listRef} id={listboxId} role="listbox" className="p-2 space-y-1 max-h-[50vh] overflow-y-auto">
                {results.map((result, index) => {
                  const TileComponent = getTileComponent(result.type);

                  return (
                    <TileComponent
                      key={result.id}
                      result={result}
                      mode="compact"
                      showScore={showScores}
                      showSource={showSources}
                      highlighted={index === selectedIndex}
                      listboxOption={{ id: optionId(result.id), selected: index === selectedIndex }}
                      onClick={() => handleResultClick(result)}
                      onAction={handleTileAction}
                    />
                  );
                })}
              </div>

              {/* Footer with hints and expand button */}
              <div className="px-3 py-2 border-t border-slate-700/50 bg-slate-800/50 flex items-center justify-between">
                <ShortcutHints hasSelection={selectedIndex >= 0} />
                <button
                  onClick={handleOpenFullModal}
                  className="flex items-center gap-1.5 px-2 py-1 text-xs text-slate-400 hover:text-cyan-400 hover:bg-slate-700/50 rounded transition-colors"
                >
                  <Maximize2 className="w-3 h-3" />
                  <span>Full search</span>
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Full Search Modal */}
      <FullSearchModal
        isOpen={isFullModalOpen}
        onClose={handleCloseFullModal}
        initialQuery={query}
        onSelect={onSelect}
        placeholder="Search everything..."
      />
    </div>
  );
}

export default InlineSearch;
